import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { PortalLink } from './portalLinks';

export interface PortalSwitcherProps {
  /** id do portal atual (marcado como "Atual"). */
  currentId: string;
  /** Links para todos os portais (incluindo o atual). */
  links: PortalLink[];
  /** Rótulos de interface i18n. */
  labels?: {
    trigger: string;
    head: string;
    current: string;
  };
}

/**
 * Seletor de portais — botão "Portais TMR" presente em toda página, revelando
 * os demais portais. Como cada portal é um deploy separado, os itens são
 * âncoras que NAVEGAM para a outra solução (o atual não é link).
 */
export function PortalSwitcher({ currentId, links, labels }: PortalSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ui = labels ?? {
    trigger: 'Portais TMR',
    head: 'Acessar outro portal',
    current: 'Atual',
  };

  return (
    <div className="switcher">
      <button type="button" className="switcher-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="switcher-grid" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </span>
        <span className="switcher-label">{ui.trigger}</span>
        <svg className={`switcher-chev ${open ? 'is-open' : ''}`} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <>
          <div className="switcher-scrim" onClick={() => setOpen(false)} />
          <div className="switcher-menu" role="menu">
            <div className="switcher-menu-head">{ui.head}</div>
            {links.map((p) => {
              const active = p.id === currentId;
              const accentStyle = { '--accent': p.accent, '--accent-soft': p.accentSoft } as CSSProperties;
              const content = (
                <>
                  <span className="switcher-dot" />
                  <span className="switcher-item-text">
                    <span className="switcher-item-name">{p.name}</span>
                    <span className="switcher-item-sub">{p.sub}</span>
                  </span>
                  {active ? (
                    <span className="switcher-current">{ui.current}</span>
                  ) : (
                    <svg className="switcher-arrow" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  )}
                </>
              );

              return active ? (
                <div key={p.id} className="switcher-item is-active" role="menuitem" aria-current="page" style={accentStyle}>
                  {content}
                </div>
              ) : (
                <a key={p.id} href={p.href} className="switcher-item" role="menuitem" style={accentStyle}>
                  {content}
                </a>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
